import { 
    Box, 
    VStack, 
    HStack, 
    Heading, 
    Text, 
    Container,
    Circle,
    Divider,
    Badge,
    Image,
    Link
} from "@chakra-ui/react"
import { useContext } from "react"
import { ThemeContext } from "../context/ThemeContext"
import { LanguageContext } from "../context/LanguageContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGraduationCap, faCalendarAlt, faCertificate, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons"

const Education = () => {
    const { theme } = useContext(ThemeContext)
    const { t, language } = useContext(LanguageContext)

    const getEducationData = () => [
        {
            id: 1,
            date: "2020 - 2025",
            title: language === 'es' ? "Ingeniería Eléctrica" : "Electrical Engineering",
            institution: "Universidad Técnica de Esmeraldas \"Luis Vargas Torres\"",
            type: "degree",
            description: language === 'es' 
                ? "Formación integral en sistemas eléctricos, control automático y tecnologías emergentes. Especialización en IoT, sistemas embebidos y automatización industrial."
                : "Comprehensive training in electrical systems, automatic control and emerging technologies. Specialization in IoT, embedded systems and industrial automation.",
            skills: language === 'es' 
                ? ["Sistemas Eléctricos", "Control Automático", "IoT", "Programación"]
                : ["Electrical Systems", "Automatic Control", "IoT", "Programming"]
        },
        {
            id: 2,
            date: "2024",
            title: "Meta Front-End Developer",
            institution: "Meta - Coursera",
            type: "certification",
            description: language === 'es'
                ? "Programa especializado completo en desarrollo front-end, cubriendo HTML, CSS, JavaScript, React y mejores prácticas de desarrollo web moderno."
                : "Complete specialized program in front-end development, covering HTML, CSS, JavaScript, React and modern web development best practices.",
            skills: ["React", "JavaScript", "HTML/CSS", "UI/UX"],
            link: "https://www.coursera.org/account/accomplishments/specialization/4S2HTCU9PBNX"
        },
        {
            id: 3,
            date: "2024",
            title: "Python for Data Science, AI & Development",
            institution: "IBM - Coursera", 
            type: "certification",
            description: language === 'es'
                ? "Fundamentos de Python aplicado a ciencia de datos, inteligencia artificial y desarrollo de aplicaciones. Análisis de datos y machine learning."
                : "Python fundamentals applied to data science, artificial intelligence and application development. Data analysis and machine learning.",
            skills: ["Python", "Data Science", "AI", "Machine Learning"],
            link: "https://www.coursera.org/account/accomplishments/records/S5VAYKKZNFBQ"
        },
        {
            id: 4,
            date: "2024",
            title: "Developing AI Applications with Python and Flask",
            institution: "IBM - Coursera",
            type: "certification", 
            description: language === 'es'
                ? "Desarrollo de aplicaciones de inteligencia artificial usando Python y Flask. Implementación de modelos de IA en aplicaciones web."
                : "Development of artificial intelligence applications using Python and Flask. Implementation of AI models in web applications.",
            skills: language === 'es' 
                ? ["Python", "Flask", "Aplicaciones IA", "Desarrollo Web"]
                : ["Python", "Flask", "AI Applications", "Web Development"],
            link: "https://www.coursera.org/account/accomplishments/records/S5VAYKKZNFBQ"
        },
        {
            id: 5,
            date: "2024",
            title: language === 'es' ? "EF SET English Certificate - C1 Avanzado" : "EF SET English Certificate - C1 Advanced",
            institution: "EF Education First",
            type: "certification",
            description: language === 'es'
                ? "Certificación de nivel avanzado en inglés (63/100), demostrando competencia en comunicación profesional y técnica en entornos internacionales."
                : "Advanced level English certification (63/100), demonstrating competence in professional and technical communication in international environments.",
            skills: language === 'es' 
                ? ["Inglés C1", "Comunicación Profesional", "Inglés Técnico"]
                : ["English C1", "Professional Communication", "Technical English"],
            link: "https://cert.efset.org/es/4eLeYk"
        }
    ]

    const educationData = getEducationData()

    const TimelineItem = ({ item, isLast }) => (
        <HStack spacing="2rem" alignItems="flex-start" marginBottom={isLast ? "0" : "3rem"}>
            {/* Línea de tiempo vertical */}
            <VStack spacing="0" alignItems="center">
                <Circle
                    size="3rem"
                    bg={item.type === "degree" ? "#2B6CB0" : "#38A169"}
                    color="white"
                    fontSize="1.2rem"
                >
                    <FontAwesomeIcon 
                        icon={item.type === "degree" ? faGraduationCap : faCertificate} 
                    />
                </Circle>
                {!isLast && (
                    <Box
                        width="2px"
                        height="4rem"
                        bg={theme.borderColor}
                        marginTop="1rem"
                    />
                )}
            </VStack>

            {/* Contenido del item */}
            <VStack spacing="1rem" alignItems="flex-start" flex="1">
                <Box
                    bg={theme.bg.card}
                    padding="1.5rem"
                    borderRadius="1rem"
                    border={`2px solid ${theme.borderColor}`}
                    width="100%"
                    _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: theme.bg.main === "#fff" 
                            ? "0 8px 25px rgba(43, 108, 176, 0.15)" 
                            : "0 8px 25px rgba(0, 217, 255, 0.15)"
                    }}
                    transition="all 0.3s ease"
                >
                    <VStack spacing="1rem" alignItems="flex-start">
                        {/* Header con fecha */}
                        <HStack justifyContent="space-between" width="100%" flexWrap="wrap">
                            <Badge
                                colorScheme={item.type === "degree" ? "cyan" : "green"}
                                fontSize="0.8rem"
                                padding="0.3rem 0.8rem"
                                borderRadius="full"
                            >
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "0.5rem" }} />
                                {item.date}
                            </Badge>
                            {item.link && (
                                <Link
                                    href={item.link}
                                    isExternal
                                    color={item.type === "degree" ? "#2B6CB0" : "#38A169"}
                                    fontSize="0.8rem"
                                    fontWeight="semibold"
                                    _hover={{ textDecoration: "underline" }}
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: "0.3rem" }} />
                                    {t('education.verify')}
                                </Link>
                            )}
                        </HStack>

                        {/* Título y institución */}
                        <VStack spacing="0.5rem" alignItems="flex-start">
                            <Heading
                                size="md"
                                color={theme.color}
                                fontWeight="bold"
                            >
                                {item.title}
                            </Heading>
                            <Text
                                fontSize="1rem"
                                color={item.type === "degree" ? "#2B6CB0" : "#38A169"}
                                fontWeight="semibold"
                            >
                                {item.institution}
                            </Text>
                        </VStack>

                        {/* Descripción */}
                        <Text
                            fontSize="0.95rem"
                            color={theme.color}
                            lineHeight="1.6"
                            opacity="0.9"
                        >
                            {item.description}
                        </Text>

                        {/* Skills/Tecnologías */}
                        {item.skills && (
                            <HStack spacing="0.5rem" flexWrap="wrap">
                                {item.skills.map((skill, index) => (
                                    <Badge
                                        key={index}
                                        bg={theme.borderColor}
                                        color={theme.color}
                                        fontSize="0.75rem"
                                        padding="0.2rem 0.6rem"
                                        borderRadius="md"
                                    >
                                        {skill}
                                    </Badge>
                                ))}
                            </HStack>
                        )}
                    </VStack>
                </Box>
            </VStack>
        </HStack>
    )

    return (
        <Box
            id="education"
            bg={theme.bg.education}
            minHeight="100vh"
            padding="5rem 0"
        >
            <Container maxW="6xl">
                <VStack spacing="4rem" alignItems="center">
                    {/* Header de la sección */}
                    <VStack spacing="1rem" textAlign="center">
                        <Heading
                            size="2xl"
                            color={theme.color}
                            fontWeight="bold"
                            marginBottom="1rem"
                        >
                            <FontAwesomeIcon 
                                icon={faGraduationCap} 
                                style={{ 
                                    marginRight: "1rem", 
                                    color: theme.bg.main === "#fff" ? "#2B6CB0" : "#00D9FF" 
                                }} 
                            />
                            {t('education.title')}
                        </Heading>
                        <Text
                            fontSize="1.2rem"
                            color={theme.color}
                            opacity="0.8"
                            maxWidth="600px"
                            lineHeight="1.6"
                        >
                            {language === 'es' 
                                ? "Mi trayectoria académica y profesional en constante evolución, con formación continua en las últimas tecnologías y metodologías del sector."
                                : "My constantly evolving academic and professional career, with continuous training in the latest technologies and methodologies in the sector."
                            }
                        </Text>
                        <Divider 
                            borderColor={theme.bg.main === "#fff" ? "#2B6CB0" : "#00D9FF"} 
                            borderWidth="2px" 
                            width="100px" 
                            marginTop="1rem"
                        />
                    </VStack>

                    {/* Timeline de educación */}
                    <Box width="100%" maxWidth="4xl">
                        <VStack spacing="0" alignItems="stretch">
                            {educationData.map((item, index) => (
                                <TimelineItem
                                    key={item.id}
                                    item={item}
                                    isLast={index === educationData.length - 1}
                                />
                            ))}
                        </VStack>
                    </Box>

                    {/* Footer de la sección */}
                    <Box
                        bg={theme.bg.card}
                        padding="2rem"
                        borderRadius="1rem"
                        border={`2px solid ${theme.borderColor}`}
                        textAlign="center"
                        maxWidth="600px"
                    >
                        <Text
                            fontSize="1rem"
                            color={theme.color}
                            fontStyle="italic"
                            opacity="0.9"
                        >
                            "{t('education.reflection')}"
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    )
}

export default Education
